import { ALL_CARDS, CARDS_BY_ID } from "@grundbesitz/shared";
import type { Card, GameEvent } from "@grundbesitz/shared";
import type { EventContext } from "./events";

/**
 * Spoken lines: card texts and short announcements. The audio is generated ahead of time
 * (scripts/generate-voice.ts) and served from /voice/<id>.mp3, so the app needs no API key.
 * Pure data and functions, shared by the generator and the client.
 */

export interface VoiceLine {
  id: string;
  /** Exactly what is spoken. */
  text: string;
  /** Delivery direction for the TTS model, never spoken. */
  style: string;
}

/** How "200 M" is read aloud. */
const SPOKEN_CURRENCY = "Mark";

export function spokenCardText(text: string): string {
  return text.replace(/(\d+) M\b/g, `$1 ${SPOKEN_CURRENCY}`);
}

function cardStyle(card: Card): string {
  switch (card.effect.type) {
    case "GAIN":
    case "COLLECT_FROM_EACH":
      return "Spielleiter verkündet eine gute Nachricht, erfreut und warm";
    case "PAY":
    case "PAY_PER_BUILDING":
      return "Spielleiter, mitfühlend mit einem Hauch Schadenfreude";
    case "GO_TO_JAIL":
      return "Spielleiter, streng und dramatisch";
    case "JAIL_FREE":
      return "Spielleiter, verschwörerisch und augenzwinkernd";
    case "CHOICE":
      return "Spielleiter, neckend, lässt die Wahl offen";
    case "MOVE_TO":
    case "MOVE_BY":
      return "Spielleiter, lebhaft und ansagend";
    default: {
      const unreachable: never = card.effect;
      return unreachable;
    }
  }
}

export const cardClipId = (cardId: string): string => `card-${cardId}`;

export const CARD_LINES: readonly VoiceLine[] = ALL_CARDS.map((card) => ({
  id: cardClipId(card.id),
  text: spokenCardText(card.text),
  style: cardStyle(card),
}));

/** Announcements with a few variants each, so repeated events do not sound canned. */
const ANNOUNCEMENTS = {
  "game-start": {
    style: "Moderator einer Spielshow, einladend und vorfreudig",
    texts: ["Willkommen bei Grundbesitz! Möge der klügste Immobilienhai gewinnen.", "Die Würfel liegen bereit. Viel Glück beim Kaufen, Bauen und Kassieren!"],
  },
  "your-turn": {
    style: "freundlich, auffordernd, kurz",
    texts: ["Du bist dran!", "Dein Zug!", "Auf geht's, du bist am Zug."],
  },
  doubles: {
    style: "begeistert",
    texts: ["Pasch! Du darfst noch einmal würfeln.", "Ein Pasch! Gleich nochmal."],
  },
  go: {
    style: "erfreut, Kasse klingelt",
    texts: ["Über LOS! Die Bank zahlt.", "LOS-Geld für dich!"],
  },
  bought: {
    style: "zufrieden, anerkennend",
    texts: ["Gekauft!", "Gute Wahl. Das gehört jetzt dir.", "Zugegriffen!"],
  },
  "rent-claim": {
    style: "aufgeregt, eindringlich flüsternd, als Tipp",
    texts: ["Miete fällig! Fordere sie ein, bevor jemand würfelt.", "Kassier die Miete, schnell, bevor gewürfelt wird!"],
  },
  "rent-pay": {
    style: "mitfühlend, leicht amüsiert",
    texts: ["Autsch. Miete.", "Das tut weh. Miete zahlen.", "Tja, Miete ist Miete."],
  },
  "free-parking": {
    style: "jubelnd, Jackpot",
    texts: ["Frei Parken! Der ganze Topf gehört dir!"],
  },
  hotel: {
    style: "beeindruckt",
    texts: ["Ein Hotel! Jetzt wird es teuer für die anderen."],
  },
  "auction-start": {
    style: "Auktionator, schwungvoll eröffnend",
    texts: ["Versteigerung! Wer bietet?", "Die Auktion ist eröffnet!"],
  },
  "auction-won": {
    style: "Auktionator, mit Hammerschlag-Gestus, schnell gesprochen",
    texts: ["Zum Ersten, zum Zweiten, zum Dritten! Verkauft an dich!"],
  },
  "trade-offer": {
    style: "neugierig, verschwörerisch",
    texts: ["Du hast ein Handelsangebot.", "Jemand will mit dir handeln."],
  },
  "trade-done": {
    style: "zufrieden, Handschlag",
    texts: ["Der Handel steht!", "Abgemacht!"],
  },
  debt: {
    style: "ernst, besorgt",
    texts: ["Du hast Schulden. Verkaufe oder beleihe etwas, um zu zahlen."],
  },
  jail: {
    style: "streng, dramatisch",
    texts: ["Ab ins Gefängnis!", "Gehe direkt ins Gefängnis!"],
  },
  "jail-triple": {
    style: "ungläubig, dann streng",
    texts: ["Drei Pasch hintereinander? Das ist verdächtig. Ab ins Gefängnis!"],
  },
  "jail-out": {
    style: "erleichtert",
    texts: ["Endlich wieder frei!", "Du bist raus aus dem Gefängnis."],
  },
  "bankrupt-self": {
    style: "bedauernd, sanft",
    texts: ["Pleite. Für dich ist das Spiel vorbei."],
  },
  "bankrupt-other": {
    style: "dramatisch, ernst",
    texts: ["Ein Mitspieler ist bankrott!"],
  },
  win: {
    style: "Spielshow-Moderator, jubelnd, triumphal",
    texts: ["Gewonnen! Herzlichen Glückwunsch, du bist der Immobilienmogul!"],
  },
  "game-over": {
    style: "anerkennend, ruhig",
    texts: ["Das Spiel ist aus. Gut gespielt!"],
  },
} satisfies Record<string, { style: string; texts: string[] }>;

export type AnnouncementId = keyof typeof ANNOUNCEMENTS;

export const ANNOUNCEMENT_LINES: readonly VoiceLine[] = Object.entries(ANNOUNCEMENTS).flatMap(([key, a]) =>
  a.texts.map((text, i) => ({ id: `${key}-${i + 1}`, text, style: a.style })),
);

export const ALL_VOICE_LINES: readonly VoiceLine[] = [...CARD_LINES, ...ANNOUNCEMENT_LINES];

/** One announcement for a batch from the viewer's side, by priority. Null when nothing is worth saying. */
export function announcementFor(events: readonly GameEvent[], ctx: EventContext): AnnouncementId | null {
  const self = ctx.selfId;
  const find = <T extends GameEvent["type"]>(type: T, pred: (e: Extract<GameEvent, { type: T }>) => boolean = () => true) =>
    events.find((e): e is Extract<GameEvent, { type: T }> => e.type === type && pred(e as Extract<GameEvent, { type: T }>));
  const cardDrawn = events.some((e) => e.type === "CARD_DRAWN");

  const over = find("GAME_OVER");
  if (over) return self !== null && over.winnerId === self ? "win" : "game-over";
  const bankrupt = find("BANKRUPT");
  if (bankrupt) return bankrupt.playerId === self ? "bankrupt-self" : "bankrupt-other";
  if (self === null) return find("AUCTION_STARTED") ? "auction-start" : find("GAME_STARTED") ? "game-start" : null;

  const jail = find("SENT_TO_JAIL", (e) => e.playerId === self);
  // A jail card is read aloud already.
  if (jail && jail.reason !== "CARD") return jail.reason === "TRIPLE_DOUBLES" ? "jail-triple" : "jail";
  if (find("AUCTION_ENDED", (e) => e.winnerId === self)) return "auction-won";
  if (find("DEBT_CREATED", (e) => e.debt.debtor === self)) return "debt";
  if (find("TRADE_PROPOSED", (e) => e.trade.to === self)) return "trade-offer";
  if (find("TRADE_ACCEPTED", (e) => e.trade.from === self || e.trade.to === self)) return "trade-done";
  if (find("RENT_DUE", (e) => e.owner === self)) return "rent-claim";
  if (find("PAYMENT", (e) => e.reason === "RENT" && e.from === self)) return "rent-pay";
  if (find("PAYMENT", (e) => e.reason === "FREE_PARKING" && e.to === self)) return "free-parking";
  if (find("BUILT", (e) => e.playerId === self && e.buildings === 5)) return "hotel";
  if (find("AUCTION_STARTED")) return "auction-start";
  if (find("GAME_STARTED")) return "game-start";
  // Small talk gives way to a card being read.
  if (cardDrawn) return null;
  if (find("LEFT_JAIL", (e) => e.playerId === self)) return "jail-out";
  if (find("PROPERTY_BOUGHT", (e) => e.playerId === self)) return "bought";
  if (find("DICE_ROLLED", (e) => e.playerId === self && e.doubles)) return "doubles";
  if (find("PLAYER_MOVED", (e) => e.playerId === self && e.passedGo)) return "go";
  if (find("TURN_STARTED", (e) => e.playerId === self)) return "your-turn";
  return null;
}

/** Clip ids to play for a batch, in order: drawn cards first, then at most one announcement. */
export function voiceFor(events: readonly GameEvent[], ctx: EventContext, rand: () => number = Math.random): string[] {
  const clips: string[] = [];
  for (const e of events) {
    if (e.type === "CARD_DRAWN" && CARDS_BY_ID[e.cardId]) clips.push(cardClipId(e.cardId));
  }
  const key = announcementFor(events, ctx);
  if (key) {
    const n = ANNOUNCEMENTS[key].texts.length;
    clips.push(`${key}-${Math.min(n, Math.floor(rand() * n) + 1)}`);
  }
  return clips;
}
